import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useSettings, useToast } from "../App";
import OptionsPanel from "../components/OptionsPanel";
import PipelineNav from "../components/PipelineNav";
import ProgressSteps from "../components/ProgressSteps";
import { api } from "../lib/api";
import { useJobStream } from "../lib/useJobStream";
import type { GenerationOptions, Job, ProjectDetail } from "../lib/types";

const DEFAULT_OPTIONS: GenerationOptions = {
  card_count: 50,
  difficulty: "mixed",
  card_types: ["basic", "cloze"],
  focus: "key_concepts",
  min_confidence: 0.55,
  grounding: "strict",
  temperature_hint: 0.3,
  duplicate_threshold: 0.86,
  organise_into_subdecks: true,
  extra_instructions: "",
};

export default function GeneratePage() {
  const { projectId = "" } = useParams();
  const { pushError, push } = useToast();
  const { settings } = useSettings();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [options, setOptions] = useState<GenerationOptions>(DEFAULT_OPTIONS);
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const detail = await api.getProject(projectId);
      setProject(detail);
      setOptions((current) => ({ ...current, ...(detail.options as GenerationOptions) }));
    } catch (error) {
      pushError(error);
    }
  }, [projectId, pushError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Reattach to a generation job that was already running when the page loaded.
  useEffect(() => {
    api
      .listJobs(projectId)
      .then((jobs) => {
        const live = jobs.find(
          (j) => (j.status === "running" || j.status === "queued") && j.kind !== "ingest",
        );
        if (live) setJobId(live.id ?? live.job_id ?? null);
      })
      .catch(() => {});
  }, [projectId]);

  const onJobDone = useCallback(
    (job: Job) => {
      refresh();
      if (job.status === "succeeded") {
        const generated = job.result?.cards_passed ?? job.result?.generation?.cards_passed;
        if (generated != null) push(`Generated ${generated} cards ready for review.`, "good");
        const warning = job.result?.warning ?? job.result?.generation?.warning;
        if (warning) push(warning);
      }
    },
    [refresh, push],
  );

  const job = useJobStream(jobId, onJobDone);
  const running = job?.status === "running" || job?.status === "queued";

  const generate = async () => {
    // Set before the first await, not after - a click needs to look like it
    // registered instantly, not once the round trip to create the job
    // finishes. api.generate() itself returns fast (job submission is
    // fire-and-forget on the backend), so this window is normally brief,
    // but "brief and invisible" still reads as "did that even work?".
    setSubmitting(true);
    try {
      await api.updateProject(projectId, { options });
      const { job_id, warning } = await api.generate(projectId, options);
      if (warning) push(warning);
      setJobId(job_id);
    } catch (error) {
      pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const ready = (project?.sources ?? []).filter((s) => s.status === "ready").length;
  const counts = project?.counts;
  const canGenerate = ready > 0 && !running && !submitting && Boolean(settings?.llm_key_present);

  if (!project) return <main className="page"><p className="muted">Loading…</p></main>;

  return (
    <main className="page">
      <PipelineNav projectId={projectId} projectName={project.name} current="generate" />

      <div className="page-head">
        <div className="grow">
          <h1>Generate cards</h1>
          <p>Nothing is exported until you've reviewed it.</p>
        </div>
        {counts && counts.cards_total > 0 && (
          <Link to={`/projects/${projectId}/review`} className="btn btn-primary btn-lg">
            Review {counts.cards_total} cards →
          </Link>
        )}
      </div>

      <div className="review-layout">
        <div className="stack">
          <div className="panel">
            <h2 style={{ marginBottom: 12 }}>Generation settings</h2>
            <OptionsPanel options={options} onChange={setOptions} disabled={running} />
          </div>
        </div>

        <div className="stack">
          {job && (
            <div className="panel">
              <ProgressSteps job={job} />
            </div>
          )}

          <div className="panel">
            <div className="row" style={{ marginBottom: 12 }}>
              <h2 className="grow">Run generation</h2>
              {counts && counts.cards_total > 0 && (
                <span className="small faint">
                  Regenerating replaces proposed cards but keeps anything you've edited or accepted.
                </span>
              )}
            </div>

            {!settings?.llm_key_present && (
              <div className="alert alert-warn" style={{ marginBottom: 12 }}>
                <span>⚠</span>
                <div>
                  <strong>AI service unavailable</strong>
                  <div className="alert-hint">
                    Card generation is temporarily down. Try again shortly.
                  </div>
                </div>
              </div>
            )}

            {ready === 0 && (
              <div className="empty">
                <p className="muted small">Upload at least one source first.</p>
                <Link to={`/projects/${projectId}/sources`} className="btn btn-primary btn-sm">
                  Go to Sources
                </Link>
              </div>
            )}

            <button className="btn-primary btn-lg" onClick={generate} disabled={!canGenerate}>
              {submitting || running ? (
                <>
                  <span className="spinner" /> {submitting && !running ? "Starting…" : "Working…"}
                </>
              ) : counts && counts.cards_total > 0 ? (
                "Regenerate cards"
              ) : (
                "Generate cards"
              )}
            </button>
            {(submitting || running) && (
              <p className="small faint" style={{ marginTop: 8, marginBottom: 0 }}>
                Concept extraction and card generation call the AI model per passage - larger
                uploads can take a minute or more. The steps above update live as each stage
                finishes.
              </p>
            )}
          </div>

          {counts && counts.cards_total > 0 && (
            <div className="panel">
              <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))" }}>
                <Stat value={counts.concepts} label="Concepts" />
                <Stat value={counts.cards_total} label="Cards" />
                <Stat value={counts.accepted} label="Accepted" />
                <Stat value={counts.flagged} label="Flagged" />
                <Stat value={counts.duplicate} label="Duplicates" />
              </div>
              <hr className="divider" />
              <div className="row">
                <Link to={`/projects/${projectId}/review`} className="btn btn-primary">
                  Review cards
                </Link>
                <Link to={`/projects/${projectId}/export`} className="btn">
                  Export to Anki
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="stat">
      <span className="value">{value}</span>
      <span className="label">{label}</span>
    </div>
  );
}
